import { addToast, Button, Modal, TextOutlinedInput } from '@octano/global-ui';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Col, Container, Row } from 'reactstrap';
import { useValidations } from '../../../../../hooks/useValidations';
import useTexts from '../hooks/useTexts';
import { ParticipantModal } from '../types';

export interface AddEmailModalRef {
  closeModal: () => void;
  openModal: () => void;
}

interface Props {
  list?: ParticipantModal[];
  onConfirm?: (next: ParticipantModal) => void;
}

export const AddEmailModal = forwardRef<AddEmailModalRef, Props>(
  ({ list, onConfirm }, ref) => {
    const forms = useForm({
      mode: 'onChange',
    });
    const {
      control,
      reset,
      handleSubmit,
      formState: { isSubmitting },
    } = forms;

    const texts = useTexts();
    const { msgValidations, validateEmail } = useValidations();
    const [modal, setModal] = useState(false);

    const handleOpen = useCallback(() => {
      reset();
      setModal(true);
    }, [reset]);

    const handleClose = useCallback(() => {
      setModal(false);
      reset();
    }, [reset]);

    useImperativeHandle(ref, () => ({
      openModal: handleOpen,
      closeModal: handleClose,
    }));

    const onSubmit = async (values: any) => {
      try {
        // Verificar duplicados
        if (
          list?.some(
            (e) =>
              !e?.email
                ?.trim()
                ?.toLowerCase()
                ?.localeCompare(values?.email?.trim()?.toLowerCase()),
          )
        ) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: texts.msg.emailDuplicated,
          });
          return;
        }

        onConfirm && onConfirm(values);
        reset();
        setModal(false);
      } catch (error: any) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.modal.error,
        });
        reset();
        setModal(false);
      }
    };

    return (
      <Modal isOpen={modal} toggle={handleClose} size="md" unmountOnClose>
        <Container fluid>
          <p className="text-dark fs-20 mt-3 mb-4 text-center">
            {texts.modal.title}
          </p>
          <p className="text-dark fs-16 mt-3 mb-4 text-center">
            {texts.modal.description}
          </p>
          <Row className="mb-5">
            <Col xs={12}>
              <TextOutlinedInput
                name="name"
                control={control}
                label={texts.modal.form.name}
                placeholder={texts.modal.form.namePlaceholder}
                rules={{
                  required: msgValidations.required,
                }}
              />
              <TextOutlinedInput
                name="email"
                control={control}
                label={texts.modal.form.email}
                placeholder={texts.modal.form.emailPlaceholder}
                rules={{
                  required: msgValidations.required,
                  validate: validateEmail,
                }}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} sm={6} className="pb-2">
              <Button
                type="button"
                onClick={handleClose}
                text={texts.modal.form.cancel}
                outlined
                fullwidth
                disabled={isSubmitting}
              />
            </Col>
            <Col xs={12} sm={6} className="pb-2">
              <Button
                type="button"
                text={texts.modal.form.submit}
                fullwidth
                loading={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              />
            </Col>
          </Row>
        </Container>
      </Modal>
    );
  },
);
